import React, { useEffect, useState, useMemo } from 'react';
import { message, Spin } from 'antd';
import {
  SafetyCertificateFilled,
  MobileOutlined,
  CopyOutlined,
  CheckCircleFilled,
  CloseCircleFilled,
  ArrowRightOutlined,
  LockOutlined,
  UnlockOutlined,
  CheckOutlined,
  ReloadOutlined,
  ThunderboltFilled,
  InfoCircleOutlined,
  SafetyCertificateOutlined,
  CheckCircleOutlined,
  ThunderboltOutlined,
  HistoryOutlined
} from '@ant-design/icons';
import numeral from 'numeral';
import { io } from 'socket.io-client';
import { v4 as uuidv4 } from 'uuid';
import { getCustomerPaymentDetails, postCustomerCollection } from './utils/ApiUtils';
import { translations } from './translations';
import './App.css';

const generatePaymentSchedule = (customerData) => {
  if (!customerData || !customerData.signingDate) return [];

  const { totalCost, totalPaid, deposit, recurrentPayment, signingDate } = customerData;
  if (!totalCost || totalCost <= 0) return [];

  let allocated = totalPaid;
  const schedule = [];
  const start = new Date(signingDate);
  
  // Strip time from 'now' for fair OVERDUE comparison
  const now = new Date();
  now.setHours(0,0,0,0);

  // 1. Initial Deposit
  let depositPaid = Math.min(allocated, deposit);
  allocated -= depositPaid;
  
  let depositStart = new Date(start);
  depositStart.setHours(0,0,0,0);
  let depositStatus = depositPaid >= deposit ? 'PAID' : (depositStart < now ? 'OVERDUE' : 'UPCOMING');
  
  schedule.push({
    id: 'deposit',
    isDeposit: true,
    date: new Date(start),
    expected: deposit,
    received: depositPaid,
    surplus: allocated > 0 && depositPaid >= deposit ? allocated : null,
    shortfall: depositPaid < deposit ? depositPaid - deposit : null,
    status: depositStatus
  });

  // 2. Installments
  let remainingDebt = totalCost - deposit;
  let instNum = 1;
  let currentDate = new Date(start);

  while (remainingDebt > 0) {
    currentDate.setDate(currentDate.getDate() + 30);
    
    let expected = Math.min(remainingDebt, recurrentPayment);
    let received = Math.min(allocated, expected);
    allocated -= received;
    
    let compareDate = new Date(currentDate);
    compareDate.setHours(0,0,0,0);
    let status = received >= expected ? 'PAID' : (compareDate < now ? 'OVERDUE' : 'UPCOMING');
    
    schedule.push({
      id: `inst-${instNum}`,
      instNum: instNum,
      date: new Date(currentDate),
      expected: expected,
      received: received,
      surplus: allocated > 0 && received >= expected ? allocated : null,
      shortfall: received < expected && compareDate < now ? received - expected : null,
      status: status
    });

    remainingDebt -= expected;
    instNum++;
    
    // Failsafe for infinite loop
    if (instNum > 200) break;
  }

  return schedule;
};

const newUUID = uuidv4();
const ioURL = 'https://api.paywisetech.africa';
const socket = io(ioURL, {
  query: { webclient: newUUID },
  transports: ['websocket'],
  path: '/socket.io/',
});

const queryParameters = new URLSearchParams(window.location.search);

export default function App() {
  // Language State - Default to 'rw' (Kinyarwanda) and persist in localStorage
  const [lang, setLang] = useState(() => {
    return localStorage.getItem('paywise_lang') || 'rw';
  });

  const t = translations[lang] || translations.rw;

  const handleLanguageChange = (newLang) => {
    setLang(newLang);
    localStorage.setItem('paywise_lang', newLang);
  };

  const [deviceTagInput, setDeviceTagInput] = useState('');
  const [payg, setPayg] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [lookupErrorTag, setLookupErrorTag] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [seeDetails, setSeeDetails] = useState(false);
  const [phoneRinging, setPhoneRinging] = useState(false);
  const [copied, setCopied] = useState(false);

  // Customer Data State
  const [customerData, setCustomerData] = useState({
    clientName: '',
    customerAccount: '',
    deviceTag: '',
    productName: '',
    dealName: '',
    status: 'ACTIVE',
    totalCost: 0,
    totalPaid: 0,
    remainingDebt: 0,
    minPayment: 612,
    recurrentPayment: 18360,
    deposit: 0,
    phone: '',
  });

  const [payingNumber, setPayingNumber] = useState('');
  const [amountToPay, setAmountToPay] = useState('');
  const [selectedPreset, setSelectedPreset] = useState('monthly');

  // URL Status parameters
  const [txRef, setTxRef] = useState(queryParameters.get('tx_ref') || '');
  const [trId, setTrId] = useState(queryParameters.get('transaction_id') || '');
  const [paymentStatus, setPaymentStatus] = useState(queryParameters.get('status') || '');
  const [activeTab, setActiveTab] = useState('pay'); // 'pay' or 'balance'

  // Detect Carrier (MTN vs Airtel)
  const carrierInfo = useMemo(() => {
    const cleaned = payingNumber.replace(/\D/g, '');
    let norm = cleaned;
    if (norm.startsWith('250')) norm = norm.substring(3);
    else if (norm.startsWith('256')) norm = norm.substring(3);
    else if (norm.startsWith('0')) norm = norm.substring(1);

    if (cleaned.startsWith('256') || (cleaned.startsWith('0') && (norm.startsWith('77') || norm.startsWith('76') || norm.startsWith('70') || norm.startsWith('75') || norm.startsWith('74')))) {
       if (norm.startsWith('77') || norm.startsWith('76')) {
           return { name: 'MTN MoMo UG', code: 'MTN_UG', class: 'mtn', color: '#ffcc00', country: 'UG', currency: 'UGX' };
       }
       if (norm.startsWith('75') || norm.startsWith('70') || norm.startsWith('74')) {
           return { name: 'Airtel Money UG', code: 'AIRTEL_UG', class: 'airtel', color: '#e11414', country: 'UG', currency: 'UGX' };
       }
       return { name: 'Mobile Money UG', code: 'MM_UG', class: 'mtn', color: '#ffcc00', country: 'UG', currency: 'UGX' };
    }

    if (norm.startsWith('78') || norm.startsWith('79')) {
      return { name: 'MTN MoMo', code: 'MTN', class: 'mtn', color: '#ffcc00', country: 'RW', currency: 'RWF' };
    }
    if (norm.startsWith('72') || norm.startsWith('73')) {
      return { name: 'Airtel Money', code: 'AIRTEL', class: 'airtel', color: '#e11414', country: 'RW', currency: 'RWF' };
    }
    return null;
  }, [payingNumber]);

  const currency = carrierInfo?.currency || 'RWF';

  useEffect(() => {
    if (carrierInfo?.country === 'UG' && lang !== 'lg') {
      handleLanguageChange('lg');
    } else if (carrierInfo?.country === 'RW' && lang === 'lg') {
      handleLanguageChange('rw');
    }
  }, [carrierInfo?.country]);


  // Format Rwanda Phone for API (2507XXXXXXXX)
  const normalizePhoneNumber = (raw) => {
    let cleaned = (raw || '').replace(/\D/g, '');
    if (cleaned.startsWith('256') || cleaned.startsWith('250')) return cleaned;
    if (cleaned.startsWith('077') || cleaned.startsWith('075') || cleaned.startsWith('070') || cleaned.startsWith('076') || cleaned.startsWith('074')) {
      return '256' + cleaned.substring(1);
    }
    if (cleaned.startsWith('0')) return '250' + cleaned.substring(1);
    if (cleaned.length === 9) {
      if (cleaned.startsWith('77') || cleaned.startsWith('75') || cleaned.startsWith('70') || cleaned.startsWith('76') || cleaned.startsWith('74')) {
        return '256' + cleaned;
      }
      return '250' + cleaned;
    }
    return cleaned;
  };

  // Copy contract number
  const copyContractNumber = () => {
    if (customerData.customerAccount) {
      navigator.clipboard.writeText(customerData.customerAccount);
      setCopied(true);
      message.success(t.copiedToast);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  // Load customer payment details
  const fetchDetails = async (tag) => {
    const targetTag = (tag || payg || deviceTagInput || '').trim();
    if (!targetTag) return;

    setIsLoading(true);
    setLookupErrorTag('');
    try {
      const response = await getCustomerPaymentDetails({ payg: targetTag });
      const raw = response?.result?.data;

      if (!raw) {
        throw new Error('NO_DATA');
      }

      const totalCost = raw.totalCost ?? raw.loanSchedule?.loanTotalCost ?? 0;
      const totalPaid = raw.totalPaid ?? 0;
      const remainingDebt = raw.remainingDebt ?? raw.debt ?? Math.max(0, totalCost - totalPaid);
      const minPayment = raw.pricingSchedule?.minPayment ?? raw.loanSchedule?.repayment ?? 612;
      const recurrentPayment = raw.pricingSchedule?.recurrentPayment ?? minPayment * 30;
      const deposit = raw.pricingSchedule?.upfrontPayment ?? raw.loanSchedule?.deposit ?? 0;

      const clientName = raw.client?.profile
        ? `${raw.client.profile.firstName || ''} ${raw.client.profile.lastName || ''}`.trim()
        : 'Paywise Customer';

      const productName = raw.product?.name || raw.dealName || 'Financed Device';
      const contractNumber = raw.contractNumber || '';
      const deviceTag = raw.paygNumber || raw.deviceTag || targetTag;
      const status = raw.status || 'ACTIVE';

      const rawPhone = raw.client?.contact?.fullNumber || raw.client?.contact?.mobile || '';
      const formattedPhone = rawPhone ? normalizePhoneNumber(rawPhone) : '';

      setCustomerData({
        clientName,
        customerAccount: contractNumber,
        deviceTag,
        productName,
        dealName: raw.dealName || '',
        status,
        totalCost,
        totalPaid,
        remainingDebt,
        minPayment,
        recurrentPayment,
        deposit,
        recentPayments: raw.recentPayments || [],
        signingDate: raw.signingDate || raw.entryDate || new Date().toISOString(),
        phone: formattedPhone,
      });

      // Check if this contract requires initial upfront deposit
      const isUpfront = deposit > 0 && totalPaid < deposit;

      // Calculate overdue amount from the schedule to decide initial preset
      const tempCustomerData = {
        totalCost, totalPaid, remainingDebt, deposit, recurrentPayment, minPayment,
        signingDate: raw.signingDate || raw.entryDate || new Date().toISOString(),
      };
      const schedule = generatePaymentSchedule(tempCustomerData);
      const overdueRows = schedule.filter(r => r.status === 'OVERDUE');
      const totalOverdue = overdueRows.reduce((sum, r) => sum + Math.abs(r.shortfall || (r.expected - r.received)), 0);
      const cappedOverdue = Math.min(totalOverdue, remainingDebt);
      const hasOverdue = !isUpfront && overdueRows.length > 0 && cappedOverdue > 0;

      const defaultAmount = isUpfront
        ? Math.min(remainingDebt, deposit - totalPaid)
        : hasOverdue
          ? cappedOverdue
          : (recurrentPayment > 0 ? recurrentPayment : minPayment * 30);

      const initialAmount = remainingDebt < defaultAmount && remainingDebt > 0
        ? remainingDebt
        : defaultAmount;

      setSelectedPreset(isUpfront ? 'upfront' : hasOverdue ? 'overdue' : 'monthly');
      setAmountToPay(initialAmount.toString());
      setPayingNumber(formattedPhone);
      setPayg(deviceTag);
      setSeeDetails(true);
      setLookupErrorTag('');
      if (targetTag && window.location.search !== `?devicetag=${targetTag}`) {
        window.history.replaceState(null, '', `/?devicetag=${targetTag}`);
      }
    } catch (err) {
      console.warn('[fetchDetails] Contract not found or not activated for tag:', targetTag);
      setLookupErrorTag(targetTag);
      setSeeDetails(false);
      if (targetTag && window.location.search !== `?devicetag=${targetTag}`) {
        window.history.replaceState(null, '', `/?devicetag=${targetTag}`);
      }
    } finally {
      setIsLoading(false);
    }
  };

  // Preset Amount Selection
  const handlePresetSelect = (type, val) => {
    setSelectedPreset(type);
    setAmountToPay(val.toString());
  };

  // Handle Amount Input Change
  const handleAmountChange = (e) => {
    const val = e.target.value;
    setAmountToPay(val);
    setSelectedPreset('custom');
  };

  const isUpfrontPending = customerData.deposit > 0 && customerData.totalPaid < customerData.deposit;

  // Initiate Payment
  const handlePay = async (e) => {
    if (e) e.preventDefault();

    const parsedAmount = parseInt(amountToPay, 10);
    if (!parsedAmount || parsedAmount <= 0) {
      return message.error(t.errorInvalidAmount);
    }

    if (isUpfrontPending && parsedAmount < (customerData.deposit - customerData.totalPaid)) {
      return message.warning(`${t.errorMinDeposit} ${numeral(customerData.deposit - customerData.totalPaid).format('0,0')}`);
    }

    if (parsedAmount > customerData.remainingDebt && customerData.remainingDebt > 0) {
      return message.warning(`${t.errorExceedsDebt} ${numeral(customerData.remainingDebt).format('0,0')}`);
    }

    const formattedPhone = normalizePhoneNumber(payingNumber);
    const isValidRw = formattedPhone.length === 12 && formattedPhone.startsWith('2507');
    const isValidUg = formattedPhone.length === 12 && formattedPhone.startsWith('2567');

    if (!isValidRw && !isValidUg) {
      return message.error(t.errorInvalidPhone);
    }

    const telcoCode = carrierInfo?.code?.includes('MTN') || formattedPhone.startsWith('25078') || formattedPhone.startsWith('25079') || formattedPhone.startsWith('25677') || formattedPhone.startsWith('25676') ? 'MTN' : 'AIRTEL';

    const payload = {
      reference: customerData.totalPaid < customerData.deposit && customerData.customerAccount
        ? `${payg}@${customerData.customerAccount}`
        : payg,
      telephone: formattedPhone,
      amount: parsedAmount.toString(),
      webclient: newUUID,
      telco: telcoCode,
    };

    setIsSubmitting(true);
    setSeeDetails(false);
    setPhoneRinging(true);

    try {
      const resp = await postCustomerCollection(payload);
      console.log('Initiated collection:', resp);
    } catch (err) {
      console.error('Payment initiation error:', err);
      setPhoneRinging(false);
      setPaymentStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const resetToForm = () => {
    setPaymentStatus('');
    setPhoneRinging(false);
    setSeeDetails(true);
  };

  // Reset to initial URL state
  const clearUrlAndRestart = () => {
    const currentTag = payg || customerData.deviceTag || queryParameters.get('devicetag') || queryParameters.get('deviceTag');
    if (currentTag) {
      window.location.replace(`/?devicetag=${currentTag}`);
    } else {
      window.location.replace('/');
    }
  };

  // Initial Load & Socket Listeners
  useEffect(() => {
    const tag = queryParameters.get('deviceTag') || queryParameters.get('devicetag');
    if (tag && tag.trim()) {
      const cleanTag = tag.trim().toUpperCase();
      setPayg(cleanTag);
      setDeviceTagInput(cleanTag);
      fetchDetails(cleanTag);
    } else {
      setSeeDetails(false);
      setIsLoading(false);
    }

    socket.on('connect', () => {
      console.log('⚡ Socket connected to Paywise Gateway:', socket.id);
    });

    socket.on('paymentStatus', (data) => {
      console.log('🔔 Received payment status update:', data);
      const { status, depositId, result } = data;

      if (depositId === newUUID) {
        if (status === 'pending') {
          setPhoneRinging(true);
        } else if (status === 'completed') {
          setPhoneRinging(false);
          const ref = result?.reference || payg;
          const tr = result?.transactionId || '';
          setTxRef(ref);
          setTrId(tr);
          setPaymentStatus('success');
          window.history.replaceState(null, '', `/?status=success&tx_ref=${ref}&transaction_id=${tr}`);
        } else if (status === 'failed') {
          setPhoneRinging(false);
          setPaymentStatus('error');
          window.history.replaceState(null, '', `/?status=error&tx_ref=${payg}`);
        }
      }
    });

    return () => {
      socket.off('connect');
      socket.off('paymentStatus');
    };
  }, []);

  // Calculate Loan Progress %
  const paidPercent = useMemo(() => {
    if (!customerData.totalCost || customerData.totalCost <= 0) return 0;
    const pct = Math.round((customerData.totalPaid / customerData.totalCost) * 100);
    return Math.min(100, Math.max(0, pct));
  }, [customerData.totalCost, customerData.totalPaid]);

  return (
    <div className="checkout-wrapper">
      <div className="ambient-orb ambient-orb-1" />
      <div className="ambient-orb ambient-orb-2" />

      <div className="checkout-card">
        {/* Header with Font-based Brand, Language Switcher & Security Badge */}
        <div className="card-header">
          <div className="brand-wordmark">
            <div className="brand-badge-icon">
              <span>PW</span>
            </div>
            <div className="brand-name-group">
              <span className="brand-title">
                <span className="brand-pay">Pay</span>
                <span className="brand-wise">Wise</span>
              </span>
              <span className="brand-slogan">Connecting You to the Future</span>
            </div>
          </div>

          <div className="header-actions">
            {/* Language Switcher */}
            <div className="lang-switch">
              <button
                type="button"
                className={`lang-btn ${lang === 'rw' ? 'active' : ''}`}
                onClick={() => handleLanguageChange('rw')}
                title="Kinyarwanda"
              >
                🇷🇼 KIN
              </button>
              <button
                type="button"
                className={`lang-btn ${lang === 'en' ? 'active' : ''}`}
                onClick={() => handleLanguageChange('en')}
                title="English"
              >
                🇬🇧 ENG
              </button>
              <button
                type="button"
                className={`lang-btn ${lang === 'lg' ? 'active' : ''}`}
                onClick={() => handleLanguageChange('lg')}
                title="Luganda"
              >
                🇺🇬 LUG
              </button>
            </div>
          </div>
        </div>

        <div className="card-body">
          {/* STATE 1A: CONTRACT NOT FOUND OR NOT ACTIVATED STATE */}
          {lookupErrorTag && !seeDetails && !isLoading && !phoneRinging && !paymentStatus && (
            <div className="not-found-container">
              <div className="not-found-icon-circle">
                <InfoCircleOutlined />
              </div>
              <h2 className="not-found-title">{t.notFoundTitle}</h2>
              <div className="not-found-chip">{lookupErrorTag}</div>
              <p className="not-found-desc">
                {t.notFoundDesc.replace('{tag}', lookupErrorTag)}
              </p>

              <div className="not-found-tip-box">
                {t.notFoundShopTip}
              </div>

              <div style={{ textAlign: 'left', marginBottom: 6, fontSize: 13, fontWeight: 700, color: 'var(--text-main)' }}>
                {t.notFoundCheckTag}
              </div>

              <div className="input-container">
                <div className="input-box-wrapper">
                  <input
                    type="text"
                    className="custom-input"
                    placeholder={t.searchPlaceholder}
                    value={deviceTagInput}
                    onChange={(e) => setDeviceTagInput(e.target.value.toUpperCase())}
                    onKeyDown={(e) => e.key === 'Enter' && fetchDetails(deviceTagInput)}
                  />
                </div>
              </div>

              <button
                className="btn-pay-now"
                onClick={() => fetchDetails(deviceTagInput)}
                disabled={!deviceTagInput.trim()}
              >
                {t.tryAnotherTagBtn} <ArrowRightOutlined />
              </button>
            </div>
          )}

          {/* STATE 1B: INITIAL SEARCH IF NO URL TAG */}
          {!lookupErrorTag && !seeDetails && !isLoading && !phoneRinging && !paymentStatus && (
            <div style={{ textAlign: 'center', padding: '16px 0' }}>
              <div style={{ marginBottom: 20 }}>
                <h2 style={{ fontSize: 22, fontWeight: 700, margin: '0 0 6px', color: '#0f172a' }}>
                  {t.searchTitle}
                </h2>
                <p style={{ fontSize: 14, color: '#64748b', margin: 0 }}>
                  {t.searchDesc}
                </p>
              </div>

              <div className="input-container">
                <div className="input-box-wrapper">
                  <input
                    type="text"
                    className="custom-input"
                    placeholder={t.searchPlaceholder}
                    value={deviceTagInput}
                    onChange={(e) => setDeviceTagInput(e.target.value.toUpperCase())}
                    onKeyDown={(e) => e.key === 'Enter' && fetchDetails(deviceTagInput)}
                  />
                </div>
              </div>

              <button
                className="btn-pay-now"
                onClick={() => fetchDetails(deviceTagInput)}
                disabled={!deviceTagInput.trim()}
              >
                {t.searchBtn} <ArrowRightOutlined />
              </button>
            </div>
          )}

          {/* LOADING STATE */}
          {isLoading && (
            <div style={{ textAlign: 'center', padding: '60px 0' }}>
              <Spin size="large" />
              <p style={{ marginTop: 16, fontSize: 14, fontWeight: 600, color: '#64748b' }}>
                {t.fetchingDetails}
              </p>
            </div>
          )}

          {/* STATE 2: ACTIVE CHECKOUT FORM */}
          {seeDetails && !phoneRinging && !paymentStatus && (
            <div>
              {/* Checkout Tabs */}
              <div className="checkout-tabs">
                <button 
                  className={`checkout-tab ${activeTab === 'pay' ? 'active' : ''}`}
                  onClick={() => setActiveTab('pay')}
                >
                  <span className="checkout-tab-icon">⚡</span> {t.payTab}
                </button>
                <button 
                  className={`checkout-tab ${activeTab === 'balance' ? 'active' : ''}`}
                  onClick={() => setActiveTab('balance')}
                >
                  <span className="checkout-tab-icon">📊</span> {t.balanceTab}
                </button>
              </div>

              {/* Hero Balance Dark Card */}
              <div className="hero-balance-card">
                <div className="hero-top-row">
                  <div className="client-meta">
                    <span className="client-name">{customerData.clientName}</span>
                    <span className="contract-tag" onClick={copyContractNumber} title="Click to copy">
                      {t.contractLabel}{customerData.customerAccount || customerData.deviceTag}
                      {copied ? <CheckOutlined style={{ color: '#10b981' }} /> : <CopyOutlined />}
                    </span>
                  </div>

                  <span className={`status-badge ${customerData.status?.toLowerCase() === 'locked' ? 'locked' : 'active'}`}>
                    {customerData.status?.toLowerCase() === 'locked' ? (
                      <><LockOutlined /> {t.statusLocked}</>
                    ) : (
                      <><UnlockOutlined /> {t.statusActive}</>
                    )}
                  </span>
                </div>

                {/* Device Pill */}
                <div className="device-chip">
                  <MobileOutlined style={{ color: '#38bdf8', fontSize: 15 }} />
                  <span>
                    <strong>{customerData.productName}</strong>
                    {customerData.dealName && ` · ${customerData.dealName}`}
                  </span>
                </div>

                {/* Conditional Balance Hero */}
                {activeTab === 'balance' && (
                  <div className="balance-display">
                    <div className="balance-label">{t.remainingDebt}</div>
                    <div className="balance-value">
                      {numeral(customerData.remainingDebt).format('0,0')}
                      <span className="balance-currency">{currency}</span>
                    </div>
                  </div>
                )}

                {/* Progress Bar */}
                <div className="loan-progress-wrapper" style={activeTab === 'pay' ? { marginTop: 15, paddingTop: 15, borderTop: '1px solid rgba(255,255,255,0.1)' } : {}}>
                  <div className="loan-progress-header">
                    <span>{t.paidLabel} <strong>{currency} {numeral(customerData.totalPaid).format('0,0')}</strong></span>
                    {activeTab === 'balance' ? (
                      <span><strong>{paidPercent}%</strong> {t.ofTotal} {currency} {numeral(customerData.totalCost).format('0,0')}</span>
                    ) : (
                      <span><strong>{paidPercent}%</strong></span>
                    )}
                  </div>
                  <div className="progress-track">
                    <div className="progress-fill" style={{ width: `${paidPercent}%` }} />
                  </div>
                </div>
              </div>

                {/* Payment Schedule & History - Only on Balance Tab */}
                {activeTab === 'balance' && (() => {
                  const schedule = generatePaymentSchedule(customerData);
                  const depositRow = schedule.find(r => r.id === 'deposit');
                  const instRows = schedule.filter(r => r.id !== 'deposit');
                  const instPaid = instRows.filter(r => r.status === 'PAID').length;
                  const instTotal = instRows.length;
                  const depositPaid = depositRow?.status === 'PAID';
                  return (
                    <>
                      {/* Summary Stats Strip */}
                      <div className="balance-stats-strip">
                        <div className="balance-stat">
                          <span className="bstat-value" style={{ color: depositPaid ? '#4ade80' : '#f87171' }}>
                            {depositPaid ? `✓ ${t.statusPaid}` : 'PENDING'}
                          </span>
                          <span className="bstat-label">{t.depositLabel}</span>
                        </div>
                        <div className="balance-stat-divider" />
                        <div className="balance-stat">
                          <span className="bstat-value">{instPaid} / {instTotal}</span>
                          <span className="bstat-label">{t.bstatInstalmentsDone}</span>
                        </div>
                        <div className="balance-stat-divider" />
                        <div className="balance-stat">
                          <span className="bstat-value">{numeral(customerData.remainingDebt).format('0,0')}</span>
                          <span className="bstat-label">{t.bstatRemaining}</span>
                        </div>
                      </div>

                      {/* Schedule Cards */}
                      <div className="schedule-section">
                        <div className="schedule-section-title">
                          <span className="schedule-section-icon">📋</span>
                          {t.scheduleTitle}
                        </div>
                        <div className="schedule-card-list">
                          {schedule.map((row) => {
                            const isPaid = row.status === 'PAID';
                            const isOverdue = row.status === 'OVERDUE';
                            const isDeposit = row.isDeposit;
                            
                            let translatedStatus = '';
                            if (isPaid) translatedStatus = t.statusPaid;
                            else if (isOverdue) translatedStatus = t.statusOverdue;
                            else translatedStatus = t.statusUpcoming;

                            return (
                              <div key={row.id} className={`schedule-card ${row.status.toLowerCase()}`}>
                                {/* Left accent line */}
                                <div className="schedule-card-accent" />
                                
                                <div className="schedule-card-body">
                                  {/* Row 1: Name + Status Badge */}
                                  <div className="sc-row sc-row-top">
                                    <div className="sc-name">
                                      {isDeposit ? '🔑' : isPaid ? '✅' : isOverdue ? '⚠️' : '📅'} {isDeposit ? t.depositLabel : t.instalmentLabel(row.instNum)}
                                    </div>
                                    <span className={`schedule-badge ${row.status.toLowerCase()}`}>
                                      {translatedStatus}
                                    </span>
                                  </div>
                                  
                                  {/* Row 2: Due date */}
                                  <div className="sc-due-date">
                                    {t.dueLabel} {row.date.toLocaleDateString(lang === 'en' ? 'en-GB' : 'rw-RW', { day: 'numeric', month: 'short', year: 'numeric' })}
                                  </div>

                                  {/* Row 3: Amounts */}
                                  <div className="sc-row sc-row-amounts">
                                    <div className="sc-amount-cell">
                                      <span className="sc-amount-label">{t.expectedLabel}</span>
                                      <span className="sc-amount-value">{numeral(row.expected).format('0,0')} <small>{currency}</small></span>
                                    </div>
                                    <div className="sc-amount-cell">
                                      <span className="sc-amount-label">{t.receivedLabel}</span>
                                      <span className={`sc-amount-value ${row.received > 0 ? 'received' : 'empty'}`}>
                                        {row.received > 0 ? `${numeral(row.received).format('0,0')} ` : '—'}
                                        {row.received > 0 && <small>{currency}</small>}
                                      </span>
                                    </div>
                                    <div className="sc-amount-cell">
                                      <span className="sc-amount-label">
                                        {row.surplus ? t.surplusLabel : row.shortfall ? t.shortfallLabel : t.balanceLabel}
                                      </span>
                                      {row.surplus ? (
                                        <span className="sc-amount-value surplus">+{numeral(row.surplus).format('0,0')} <small>{currency}</small></span>
                                      ) : row.shortfall ? (
                                        <span className="sc-amount-value shortfall">{numeral(row.shortfall).format('0,0')} <small>{currency}</small></span>
                                      ) : (
                                        <span className="sc-amount-value empty">—</span>
                                      )}
                                    </div>
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    </>
                  );
                })()}

              {/* Payment Section - Only show on Pay Tab */}
              {activeTab === 'pay' && (
                customerData.remainingDebt <= 0 ? (
                  <div className="fully-paid-state" style={{ padding: '40px 20px', textAlign: 'center' }}>
                    <div className="fully-paid-icon" style={{ fontSize: '48px', marginBottom: '16px' }}>🎉</div>
                    <h3 style={{ margin: '0 0 8px 0', color: '#10b981', fontSize: '20px' }}>{t.fullyPaidTitle}</h3>
                    <p style={{ margin: 0, color: '#64748b', fontSize: '14px', lineHeight: 1.5 }}>{t.fullyPaidDesc}</p>
                  </div>
                ) : (
                <div className="payment-form">
                  <div className="form-header">
                    <h3>{t.selectAmountTitle}</h3>
                    {isUpfrontPending && (
                      <div className="upfront-notice-banner" style={{ marginTop: 12 }}>
                        <InfoCircleOutlined style={{ marginRight: 6 }} />
                        {t.upfrontNotice}
                      </div>
                    )}
                  </div>

                  {isUpfrontPending ? (
                    <div className="amount-presets-grid upfront-grid">
                      {/* Preset 1: Upfront Deposit */}
                      <div
                        className={`preset-chip ${selectedPreset === 'upfront' ? 'active' : ''}`}
                        onClick={() => handlePresetSelect('upfront', Math.min(customerData.remainingDebt, customerData.deposit - customerData.totalPaid))}
                      >
                        <span className="preset-title">{t.upfrontDeposit}</span>
                        <span className="preset-amount">{numeral(Math.min(customerData.remainingDebt, customerData.deposit - customerData.totalPaid)).format('0,0')}</span>
                      </div>

                      {/* Preset 2: Full Balance */}
                      <div
                        className={`preset-chip ${selectedPreset === 'full' ? 'active' : ''}`}
                        onClick={() => handlePresetSelect('full', customerData.remainingDebt)}
                      >
                        <span className="preset-title">{t.payFull}</span>
                        <span className="preset-amount">{numeral(customerData.remainingDebt).format('0,0')}</span>
                      </div>
                    </div>
                  ) : (() => {
                    // Calculate total overdue amount across all overdue instalments
                    const schedule = generatePaymentSchedule(customerData);
                    const overdueRows = schedule.filter(r => r.status === 'OVERDUE');
                    const overdueCount = overdueRows.length;
                    const totalOverdue = overdueRows.reduce((sum, r) => sum + Math.abs(r.shortfall || (r.expected - r.received)), 0);
                    const cappedOverdue = Math.min(totalOverdue, customerData.remainingDebt);

                    return (
                      <>
                        {/* Overdue Catch-Up Banner + Chip */}
                        {overdueCount > 0 && (() => {
                          return (
                            <div className="overdue-alert-section">
                              <div className="overdue-alert-banner">
                                ⚠️ {t.overdueAlert(overdueCount)}
                              </div>
                              <div
                                className={`preset-chip overdue-chip ${selectedPreset === 'overdue' ? 'active' : ''}`}
                                onClick={() => handlePresetSelect('overdue', cappedOverdue)}
                              >
                                <span className="preset-title">{t.catchUp(overdueCount)}</span>
                                <span className="preset-amount">{numeral(cappedOverdue).format('0,0')}</span>
                              </div>
                            </div>
                          );
                        })()}

                        <div className="amount-presets-grid">
                          {/* Preset 1: Monthly (Default) */}
                          <div
                            className={`preset-chip ${selectedPreset === 'monthly' ? 'active' : ''}`}
                            onClick={() => handlePresetSelect('monthly', Math.min(customerData.remainingDebt, customerData.recurrentPayment))}
                          >
                            <span className="preset-title">{t.monthly}</span>
                            <span className="preset-amount">{numeral(Math.min(customerData.remainingDebt, customerData.recurrentPayment)).format('0,0')}</span>
                          </div>

                          {/* Preset 2: 1 Week */}
                          <div
                            className={`preset-chip ${selectedPreset === 'week' ? 'active' : ''}`}
                            onClick={() => handlePresetSelect('week', Math.min(customerData.remainingDebt, customerData.minPayment * 7))}
                          >
                            <span className="preset-title">{t.oneWeek}</span>
                            <span className="preset-amount">{numeral(Math.min(customerData.remainingDebt, customerData.minPayment * 7)).format('0,0')}</span>
                          </div>

                          {/* Preset 3: Min Daily */}
                          <div
                            className={`preset-chip ${selectedPreset === 'min' ? 'active' : ''}`}
                            onClick={() => handlePresetSelect('min', customerData.minPayment)}
                          >
                            <span className="preset-title">{t.dailyMin}</span>
                            <span className="preset-amount">{numeral(customerData.minPayment).format('0,0')}</span>
                          </div>

                          {/* Preset 4: Full Balance */}
                          <div
                            className={`preset-chip ${selectedPreset === 'full' ? 'active' : ''}`}
                            onClick={() => handlePresetSelect('full', customerData.remainingDebt)}
                          >
                            <span className="preset-title">{t.payFull}</span>
                            <span className="preset-amount">{numeral(customerData.remainingDebt).format('0,0')}</span>
                          </div>
                        </div>
                      </>
                    );
                  })()}


                  {/* Custom Amount Input */}
                  <div className="input-container">
                    <div className="input-label">
                      <span>{t.customAmountLabel}</span>
                      {selectedPreset === 'custom' && (
                        <span style={{ color: '#0284c7', fontSize: 11, fontWeight: 700 }}>{t.customTag}</span>
                      )}
                    </div>
                    <div className="input-box-wrapper">
                      <span className="input-prefix-tag">{currency}</span>
                      <input
                        type="number"
                        className="custom-input with-prefix"
                        value={amountToPay}
                        onChange={handleAmountChange}
                        min="100"
                        placeholder={t.customPlaceholder}
                      />
                    </div>
                  </div>

                  {/* Phone Number Input with Telco Badge */}
                  <div className="input-container">
                    <div className="input-label">
                      <span>{t.phoneLabel}</span>
                      <span style={{ fontSize: 11, color: '#64748b' }}>{t.phoneSub}</span>
                    </div>
                    <div className="input-box-wrapper">
                      <input
                        type="tel"
                        className={`custom-input ${carrierInfo ? 'with-suffix-badge' : ''}`}
                        value={payingNumber}
                        onChange={(e) => setPayingNumber(e.target.value)}
                        placeholder={t.phonePlaceholder}
                      />
                      {carrierInfo && (
                        <div className={`carrier-badge ${carrierInfo.class}`}>
                          <CheckCircleFilled />
                          <span>{carrierInfo.name}</span>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Submit Button */}
                  <button
                    className="btn-pay-now"
                    onClick={handlePay}
                    disabled={isSubmitting || !amountToPay || parseInt(amountToPay, 10) <= 0}
                  >
                    <span>
                      {t.payBtn} {numeral(amountToPay || 0).format('0,0')}
                      {carrierInfo ? ` ${t.withCarrier} ${carrierInfo.name}` : ''}
                    </span>
                    <ArrowRightOutlined />
                  </button>

                  {/* Trust Footer */}
                  <div className="trust-footer">
                    <div className="trust-item">
                      <SafetyCertificateFilled style={{ color: '#10b981' }} />
                      <span>{t.verifiedBy}</span>
                    </div>
                    <div className="trust-item">
                      <InfoCircleOutlined />
                      <span>{t.instantUnlock}</span>
                    </div>
                  </div>
                  </div>
                )
              )}
            </div>
          )}

          {/* STATE 3: WAITING FOR PIN RADAR SCREEN */}
          {phoneRinging && (
            <div className="waiting-container">
              <div className="radar-circle-wrapper">
                <div className="radar-wave" />
                <div className="radar-wave" />
                <div className="radar-wave" />
                <div className="radar-center-icon">
                  <MobileOutlined />
                </div>
              </div>

              <h3 className="waiting-title">{t.authorizingTitle}</h3>
              <p className="waiting-subtitle">
                {t.authorizingSubtitle
                  .replace('{amount}', numeral(amountToPay).format('0,0'))
                  .replace('{phone}', payingNumber)}
              </p>

              <div className="waiting-steps-card">
                <div className="step-row">
                  <div className="step-number">1</div>
                  <div className="step-text">{t.step1}</div>
                </div>
                <div className="step-row">
                  <div className="step-number">2</div>
                  <div className="step-text">{t.step2}</div>
                </div>
                <div className="step-row">
                  <div className="step-number">3</div>
                  <div className="step-text">{t.step3}</div>
                </div>
              </div>

              <button className="btn-cancel-waiting" onClick={resetToForm}>
                {t.cancelWaitingBtn}
              </button>
            </div>
          )}

          {/* STATE 4: SUCCESS RESULT SCREEN */}
          {paymentStatus === 'success' && (
            <div className="result-card">
              <div className="result-icon-circle success">
                <CheckCircleFilled />
              </div>

              <h2 style={{ fontSize: 22, fontWeight: 700, margin: '0 0 6px', color: '#0f172a' }}>
                {t.successTitle}
              </h2>
              <p style={{ fontSize: 14, color: '#64748b', margin: '0 0 16px' }}>
                {t.successSubtitle}
              </p>

              <div className="receipt-box">
                {txRef && (
                  <div className="receipt-row">
                    <span className="receipt-label">{t.receiptRef}</span>
                    <span className="receipt-val">{txRef}</span>
                  </div>
                )}
                {trId && (
                  <div className="receipt-row">
                    <span className="receipt-label">{t.receiptTrId}</span>
                    <span className="receipt-val" style={{ fontSize: 12 }}>{trId}</span>
                  </div>
                )}
                <div className="receipt-row">
                  <span className="receipt-label">{t.receiptChannel}</span>
                  <span className="receipt-val">{carrierInfo?.name || 'Mobile Money'}</span>
                </div>
                <div className="receipt-row">
                  <span className="receipt-label">{t.receiptStatus}</span>
                  <span className="receipt-val" style={{ color: '#10b981', fontWeight: 700 }}>
                    {t.receiptCompleted}
                  </span>
                </div>
              </div>

              <button className="btn-pay-now" onClick={clearUrlAndRestart}>
                <ReloadOutlined /> {t.backToContractBtn}
              </button>
            </div>
          )}

          {/* STATE 5: ERROR RESULT SCREEN */}
          {paymentStatus === 'error' && (
            <div className="result-card">
              <div className="result-icon-circle error">
                <CloseCircleFilled />
              </div>

              <h2 style={{ fontSize: 22, fontWeight: 700, margin: '0 0 6px', color: '#0f172a' }}>
                {t.failedTitle}
              </h2>
              <p style={{ fontSize: 14, color: '#64748b', margin: '0 0 16px' }}>
                {t.failedSubtitle}
              </p>

              <div className="receipt-box" style={{ background: '#fff5f5', borderColor: '#fed7d7' }}>
                <p style={{ fontSize: 13, color: '#c53030', margin: 0, lineHeight: 1.6 }}>
                  {t.errorTip1}<br />
                  {t.errorTip2}<br />
                  {t.errorTip3}
                </p>
              </div>

              <button className="btn-pay-now" onClick={resetToForm}>
                <ReloadOutlined /> {t.tryAgainBtn}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
